import { normalizeTypeToken } from './task/base'

/**
 * Which task type a ticket becomes when it is promoted or split into tasks.
 *
 * A ticket type can name a default task type (`defaultTaskTypeDefId`, set in
 * Ticket Types settings or saved from the create-task dialogs). Otherwise a
 * task type whose key or name matches the ticket type is used, so "Bug"
 * tickets become "Bug" tasks without any setup. Shared so the dialogs'
 * preselect and the server's per-ticket resolution never drift apart.
 *
 * Full order, applied by callers: an explicit choice in the dialog, then this
 * resolution, then the task group's default and the workspace default (the
 * task create fallback in @scrambl/database `resolveTaskTypeDefId`).
 */

export type TicketTaskTypeSource = 'mapping' | 'match'

export interface TicketTaskTypeTicketType {
  key: string
  name: string
  defaultTaskTypeDefId?: string | null
}

export interface TicketTaskTypeCandidate {
  id: string
  key: string
  name: string
  isEnabled?: boolean
  isArchived?: boolean
}

export interface TicketTaskTypeResolution {
  /** The task type to use, or null to fall through to group/workspace defaults. */
  taskTypeDefId: string | null
  source: TicketTaskTypeSource | null
  /**
   * The ticket type names a default task type that the destination does not
   * offer (not in the project's task types, disabled or archived), so the
   * resolution fell back. Dialogs explain this instead of failing silently.
   */
  mappingUnavailable: boolean
}

/**
 * @param ticketType The promoted ticket's type, if any.
 * @param candidates Task types the destination offers (the project's subset,
 *   or every workspace task type when no project is chosen). Disabled and
 *   archived entries are ignored.
 */
export function resolveTicketTaskType(
  ticketType: TicketTaskTypeTicketType | null | undefined,
  candidates: readonly TicketTaskTypeCandidate[],
): TicketTaskTypeResolution {
  const usable = candidates.filter(
    candidate => candidate.isEnabled !== false && candidate.isArchived !== true,
  )
  if (!ticketType) {
    return { taskTypeDefId: null, source: null, mappingUnavailable: false }
  }

  const mappedId = ticketType.defaultTaskTypeDefId ?? null
  if (mappedId && usable.some(candidate => candidate.id === mappedId)) {
    return {
      taskTypeDefId: mappedId,
      source: 'mapping',
      mappingUnavailable: false,
    }
  }

  const key = normalizeTypeToken(ticketType.key)
  const name = normalizeTypeToken(ticketType.name)
  const match = usable.find(candidate => {
    const candidateKey = normalizeTypeToken(candidate.key)
    const candidateName = normalizeTypeToken(candidate.name)
    return (
      (key !== '' && candidateKey === key) ||
      (name !== '' && candidateName === name)
    )
  })

  return {
    taskTypeDefId: match?.id ?? null,
    source: match ? 'match' : null,
    mappingUnavailable: Boolean(mappedId),
  }
}
